import React, { useRef, useState } from "react";
import { Box, Button, IconButton } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import EditIcon from "@mui/icons-material/Edit";

import Titles from "../../shared/components/Titles/Titles";
import Badge from "../../shared/assets/badges/image.png";
import { ReactComponent as TagIcon } from "../../shared/assets/Icons/svg/TagIcon.svg";
import { ReactComponent as CampaignIcon } from "../../shared/assets/Icons/svg/CampaignIcon.svg";
import WhatsAppIcon from "./whatsappIcon.png";
import ThemeColors from "../../shared/components/ThemeColors/ThemeColors";
import AvatarIA from "../../shared/assets/images/robo.png";
import AlternativeAvatar from "../../shared/assets/images/avatar-padrao.png";

import { useAuth } from "../../app/providers/AuthProvider";
import use123zapStore from "../../context/123zapStore";
import { notify, translatePlan } from "../../shared/utils/utils";
import { ChangeProfileImage } from "../../services/api/Profile/ChangeProfileImage";
import { useSidebarDarkMode } from "../../layouts/private/components/sidebar/sidebarColorMode";

const welcomePalettes = {
  dark: {
    topBackground: "linear-gradient(135deg, #F4F0FC 0%, #ECE5F8 100%)",
    topBorder: "#DDD4EE",
    bottomBackground: "linear-gradient(125deg, #1D0A42 0%, #311064 100%)",
    bottomBorder: "#2A0B59",
    title: "#292A33",
    subtitle: "#47404F",
    buttonShadow: "0 8px 15px rgba(93, 43, 187, 0.2)",
  },
  light: {
    topBackground: "linear-gradient(135deg, #F8FAFC 0%, #EEF4F8 100%)",
    topBorder: "#DAE5ED",
    bottomBackground: "linear-gradient(125deg, #1D0A42 0%, #311064 100%)",
    bottomBorder: "#2A0B59",
    title: "#192333",
    subtitle: "#445669",
    buttonShadow: "0 8px 15px rgba(93, 43, 187, 0.16)",
  },
};

const Welcome = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  const inputFileRef = useRef(null);

  const { user, refreshUser } = useAuth();
  const planoAtual = use123zapStore((state) => state.planoAtual);
  const sidebarDark = useSidebarDarkMode();
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [isAvatarLoading, setIsAvatarLoading] = useState(false);

  const avatarSrc = avatarPreview || user?.picture || AlternativeAvatar;
  const palette = sidebarDark ? welcomePalettes.dark : welcomePalettes.light;

  const openWhatsApp = () => {
    window.open(
      "https://wa.me/551151042151?text=Ol%C3%A1%2C%20estou%20precisando%20de%20suporte!",
      "_blank"
    );
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error("Erro ao ler o arquivo"));
    });

  const handleAvatarUpload = async (event) => {
    const file = event?.target?.files?.[0];
    event.target.value = "";

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      notify("Error", "Selecione um arquivo de imagem válido.", true);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      notify("Error", "A imagem deve ter no máximo 5MB.", true);
      return;
    }

    try {
      setIsAvatarLoading(true);
      const imageBase64 = await fileToBase64(file);
      const response = await ChangeProfileImage(imageBase64);

      if (!response || typeof response === "string" || response?.status !== "success") {
        throw new Error(
          response?.message ||
            "Não foi possível atualizar a foto."
        );
      }

      setAvatarPreview(imageBase64);
      await refreshUser(true);
      notify("Success", "Foto de perfil atualizada com sucesso!", true);
    } catch (error) {
      notify(
        "Error",
        error?.message || "Não foi possível atualizar a foto de perfil.",
        true
      );
    } finally {
      setIsAvatarLoading(false);
    }
  };

  return (
    <Box
      flex={1}
      display="flex"
      flexDirection="column"
      sx={{
        height: { xs: "auto", lg: "292px", xl: "300px" },
      }}
    >
      {/* BLOCO SUPERIOR */}
      <Box
        sx={{
          background: palette.topBackground,
          flex: 1,
          padding: { xs: "16px", sm: "20px", xl: "24px" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          border: `1px solid ${palette.topBorder}`,
          borderRadius: "12px",
          borderBottomLeftRadius: 0,
          borderBottomRightRadius: 0,
        }}
      >
        <Box display="flex" justifyContent="space-between" minWidth="100%">
          <Box display="flex" gap="15px">
            <Box
              sx={{
                position: "relative",
                width: { xs: "42px", sm: "48px", xl: "56px" },
                height: { xs: "42px", sm: "48px", xl: "56px" },
              }}
            >
              <Box
                component="img"
                src={avatarSrc}
                alt="Avatar"
                sx={{
                  borderRadius: "12px",
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  border: "2px solid rgba(255,255,255,0.7)",
                }}
              />

              <IconButton
                size="small"
                onClick={() => inputFileRef.current?.click()}
                disabled={isAvatarLoading}
                aria-label="Alterar foto de perfil"
                sx={{
                  position: "absolute",
                  right: "-6px",
                  bottom: "-6px",
                  width: { xs: "20px", xl: "22px" },
                  height: { xs: "20px", xl: "22px" },
                  backgroundColor: "rgba(31, 8, 68, 0.55)",
                  color: "#F5F3FF",
                  border: "1px solid rgba(255, 255, 255, 0.5)",
                  backdropFilter: "blur(3px)",
                  transition: "all .2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(31, 8, 68, 0.75)",
                  },
                  "&.Mui-disabled": {
                    color: "rgba(245, 243, 255, 0.9)",
                    backgroundColor: "rgba(31, 8, 68, 0.6)",
                  },
                }}
              >
                {isAvatarLoading ? (
                  <CircularProgress size={12} thickness={5} color="inherit" />
                ) : (
                  <EditIcon sx={{ fontSize: "12px" }} />
                )}
              </IconButton>

              <input
                type="file"
                ref={inputFileRef}
                accept="image/*"
                onChange={handleAvatarUpload}
                style={{ display: "none" }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                gap: { xs: "2px", sm: "4px" },
                minHeight: { xs: "42px", sm: "48px", xl: "56px" },
              }}
            >
              <Titles
                styles={{ fontSize: { xl: "14px", xs: "11px", sm: "13px" } }}
                color={palette.subtitle}
                font="medium"
              >
                Olá, {user?.username || "Usuário"}!
              </Titles>

              <Titles
                styles={{ fontSize: { xl: "25px", xs: "19px", sm: "22px" } }}
                color={palette.title}
                font="medium"
              >
                {path === "/painel"
                  ? "O que iremos fazer hoje?"
                  : "O que iremos analisar hoje?"}
              </Titles>
            </Box>
          </Box>
        </Box>

        {/* ACTION BUTTONS */}
        <Box
          display="flex"
          justifyContent="space-between"
          gap="12px"
          sx={{ flexDirection: { xs: "column", xl: "row" } }}
        >
          <Box
            sx={{
              ...actionStyle,
              boxShadow: palette.buttonShadow,
              marginTop: { xs: "5px", sm: "10px", lg: "0px" },
            }}
            onClick={() =>
              path === "/painel"
                ? navigate("/campanhas/adicionar")
                : navigate("/campanhas")
            }
          >
            <CampaignIcon width="18px" height="18px" fill="#EDE8FF" />
            <Titles
              color="#EDE8FF"
              styles={{
                fontSize: {
                  xl: "16px",
                  lg: "12px",
                  md: "16px",
                  sm: "16px",
                  xs: "10px",
                },
              }}
            >
              {path === "/painel"
                ? "Criar uma nova campanha"
                : "Gerenciar campanhas"}
            </Titles>
          </Box>

          <Box
            sx={{
              ...actionStyle,
              boxShadow: palette.buttonShadow,
              marginTop: { xs: "5px", sm: "10px", lg: "0px" },
            }}
            onClick={() =>
              path === "/painel"
                ? navigate("/canais/adicionar")
                : navigate("/canais")
            }
          >
            <TagIcon width="18px" height="18px" fill="#EDE8FF" />
            <Titles
              color="#EDE8FF"
              styles={{
                fontSize: {
                  xl: "16px",
                  lg: "12px",
                  md: "16px",
                  sm: "16px",
                  xs: "10px",
                },
              }}
            >
              {path === "/painel"
                ? "Adicionar um novo canal"
                : "Visualizar meus canais"}
            </Titles>
          </Box>
        </Box>
      </Box>

      {/* BLOCO INFERIOR */}
      <Box
        sx={{
          background: palette.bottomBackground,
          height: { xs: "auto", sm: "84px", xl: "88px" },
          padding: { xs: "12px 16px", sm: "0 22px" },
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: "10px", sm: 0 },
          border: `1px solid ${palette.bottomBorder}`,
          borderRadius: "12px",
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
        }}
      >
        <Box display="flex" gap="10px" alignItems="center">
          <Box
            component="img"
            src={path === "/painel" ? Badge : AvatarIA}
            alt="Plano"
            sx={{
              width: {
                xs: path === "/painel" ? "46px" : "72px",
                xl: path === "/painel" ? "50px" : "78px",
              },
              height: {
                xs: path === "/painel" ? "46px" : "72px",
                xl: path === "/painel" ? "50px" : "78px",
              },
            }}
          />

          <Box display="flex" flexDirection="column" justifyContent="center" minHeight="32px">
            <Titles
              color="#F5F3FF"
              styles={{ fontSize: { xl: "16px", xs: "13px" } }}
            >
              {path === "/painel" ? "Plano atual" : "Inteligência"}
            </Titles>

            {planoAtual !== null ? (
              <Titles
                color="#F5F3FF"
                styles={{ fontSize: { xl: "20px", xs: "16px" } }}
              >
                {path === "/painel" ? translatePlan(planoAtual) : "Zapi!"}
              </Titles>
            ) : (
              <LinearProgress
                sx={{
                  height: "10px",
                  borderRadius: "6px",
                  minWidth: "50px",
                  width: "100%",
                  backgroundColor: ThemeColors.purple800,
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: ThemeColors.purple900,
                  },
                }}
              />
            )}
          </Box>
        </Box>

        <Button sx={supportButtonStyle} onClick={openWhatsApp}>
          <Box
            component="img"
            src={WhatsAppIcon}
            sx={{
              width: { xs: "18px", xl: "20px" },
              height: { xs: "23px", xl: "25px" },
              objectFit: "contain",
            }}
          />
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            Falar com o Suporte
          </Box>
        </Button>
      </Box>
    </Box>
  );
};

const actionStyle = {
  background: "linear-gradient(90deg, #7F34F2 0%, #6D24DF 100%)",
  color: "#EDE8FF",
  borderRadius: "10px",
  minHeight: { xs: "37px", sm: "40px", xl: "42px" },
  height: { xs: "auto", xl: "42px" },
  display: "flex",
  alignItems: "center",
  gap: "7px",
  flex: "1",
  padding: {
    xs: "9px 10px 9px 8px",
    sm: "0 0 0 8px",
    xl: "0 0 0 10px",
  },
  cursor: "pointer",
  transition: "transform .2s ease, box-shadow .2s ease, filter .2s ease",
  "&:hover": {
    filter: "brightness(1.02)",
    transform: "translateY(-1px)",
    boxShadow: "0 12px 20px rgba(93, 43, 187, 0.3)",
  },
};

const supportButtonStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "10px",
  background: "rgba(245, 243, 255, 0.95)",
  borderRadius: "10px",
  border: "1px solid rgba(255, 255, 255, 0.4)",
  width: { xs: "100%", sm: "auto" },
  padding: { xs: "6px 12px", sm: "9px 16px", xl: "10px 20px" },
  color: "#1F0844",
  fontSize: { xs: "11px", sm: "13px", xl: "14px" },
  minWidth: { xs: "44px", sm: "unset" },
  fontWeight: 600,
  transition: "transform .2s ease, background .2s ease",
  "&:hover": {
    background: "#FFFFFF",
    transform: "translateY(-1px)",
  },
};

export default Welcome;
